import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { PlatformTypes } from '@om-fe/utils';
import { AppState, BemModifiersMap } from '@om-fe/core/core/types';
import NavItem from './nav-item';
import getSlice from '@om-fe/core/utils/get-slice';
import preLoader from '@om-fe/core/core/bootstrap/preload';
import { Slider } from '@om-fe/widgets/dist/components/slider/slider';
import { CounterType, DisciplineLiveStatus, DisciplinesType } from '@om-fe/models';
import { getTopBets, removeTopBets } from './nav.actions';
import { getTopBetSliceName } from './nav.utils';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { NavListStateModel, NavListItemModel } from './nav.reducer';
import { TopBetsTab } from '../../utils/top-bets.constants';
import {
    TopBetsStorageData,
    TopBetsRouteParams,
} from '../../utils/top-bets.utils';
import { useParams } from 'react-router';
import { getVirtualStateExtraParams, setVirtualStateExtraParams } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';

export interface TopBetNavPublicProps {
    readonly disciplineId: string;
    readonly selectedId?: string;
    readonly instanceId: string;
    readonly counterType?: CounterType.numberOfLiveEvents;
    readonly liveStatus?: DisciplineLiveStatus;
    readonly activeTab?: string;
    readonly preventRedirect?: boolean;
    readonly noRedirectFilter?: (genericId?: string) => void;
    readonly genericId?: string;
    readonly isCategory?: boolean;
    readonly hasAllButton?: boolean;
    readonly hasTopBetsButton?: boolean;
    readonly topBetsNavReady?: (topBetsNavId: string) => void;
    readonly onNavItemsReady?: (items: Record<string, NavListItemModel>) => void;
    readonly isEsportsPage?: boolean;
}

interface TopBetNavStoreProps {
    readonly list?: string[];
    readonly ready?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly lang?: string;
    readonly disciplineId?: string;
    readonly items?: Record<string, NavListItemModel>;
}

export type TopBetProps = TopBetNavPublicProps & TopBetNavStoreProps;

function NavComponent ({
    deviceType,
    list,
    preventRedirect,
    noRedirectFilter,
    activeTab,
    instanceId,
    hasTopBetsButton,
    hasAllButton,
    topBetsNavReady,
    onNavItemsReady,
    isCategory,
    disciplineId,
    isEsportsPage,
    items,
    ready
}: TopBetProps) {
    const { locationRoute } = useParams<TopBetsRouteParams>();
    const [activeTabTopbets, setActiveTabTopbets] = useState<string | undefined>(activeTab);
    const [initialRoute, setInitialRoute] = useState<boolean>(false);

    useEffect(() => {
        const topbetList: string[] = [TopBetsTab.TopBets, TopBetsTab.All, ...list];
        const activeTabNav: string | undefined = activeTab === TopBetsTab.ElectronicSports ? list[0] : activeTab;
        if (!initialRoute) {
            if (locationRoute) {
                setInitialRoute(true);
                const locationRouteId: boolean = topbetList.includes(locationRoute);
                if (!locationRouteId) {
                    setActiveTabTopbets(disciplineId === DisciplinesType.ElectronicSports ? list[0] || TopBetsTab.ElectronicSports : TopBetsTab.TopBets);
                    setVirtualStateExtraParams({
                        locationId: disciplineId === DisciplinesType.ElectronicSports ? list[0] || TopBetsTab.ElectronicSports : TopBetsTab.TopBets,
                        matchLimit: 0,
                    });
                    topBetsNavReady(disciplineId === DisciplinesType.ElectronicSports ? list[0] : TopBetsTab.TopBets);
                } else {
                    setActiveTabTopbets(locationRoute);
                    setVirtualStateExtraParams({ locationId: locationRoute, matchLimit: 0 });
                    topBetsNavReady(locationRoute);
                }
            } else {
                const storage: TopBetsStorageData = getVirtualStateExtraParams<TopBetsStorageData>();
                const locationStorage: boolean = topbetList.includes(storage?.locationId!);
                if (isEsportsPage) {
                    setActiveTabTopbets(locationStorage ? storage?.locationId : activeTabNav ?? list[0]);
                    setVirtualStateExtraParams({ locationId: locationStorage ? storage?.locationId : activeTabNav ?? list[0]});
                    topBetsNavReady(locationStorage ? storage?.locationId : activeTabNav ?? list[0]);
                } else {
                    setActiveTabTopbets(locationStorage ? storage?.locationId : activeTabNav);
                    setVirtualStateExtraParams({ locationId: locationStorage ? storage?.locationId : activeTabNav });
                    topBetsNavReady(locationStorage ? storage?.locationId : activeTabNav);
                }
            }
        } else {
            setActiveTabTopbets(activeTabNav);
            setVirtualStateExtraParams({ locationId: activeTabNav, matchLimit: 0 });
            topBetsNavReady(activeTabNav);
        }
    }, [activeTab, initialRoute]);

    useEffect(() => {
        if (ready && items && Object.keys(items).length > 0 && onNavItemsReady) {
            onNavItemsReady(items);
        }
    }, [ready, items, onNavItemsReady]);

    const locationList: React.ReactNode[] = [];

    if (hasTopBetsButton && list?.length && disciplineId !== DisciplinesType.ElectronicSports) {
        locationList.push(
            <NavItem
                instanceId={instanceId}
                key={TopBetsTab.TopBets}
                id={TopBetsTab.TopBets}
                activeTab={activeTabTopbets}
                preventRedirect={preventRedirect}
                noRedirectFilter={noRedirectFilter}
                routeName={RouteNames}
                disciplineId={disciplineId}
                extraClassName={`${isCategory ? 'CategoryId' : 'LocationId'}--${TopBetsTab.TopBets}`}
            />
        )
    }
    if (hasAllButton && list?.length && disciplineId !== DisciplinesType.ElectronicSports) {
        locationList.push(
            <NavItem
                instanceId={instanceId}
                key={TopBetsTab.All}
                id={TopBetsTab.All}
                activeTab={activeTabTopbets}
                routeName={RouteNames}
                preventRedirect={preventRedirect}
                noRedirectFilter={noRedirectFilter}
                disciplineId={disciplineId}
                extraClassName={`${isCategory ? 'CategoryId' : 'LocationId'}--${TopBetsTab.All}`}
            />
        )
    }

    if (list && list?.length > 0) {
        list?.forEach((id: string) => (
            locationList.push(
                <NavItem
                    instanceId={instanceId}
                    key={id}
                    id={id}
                    activeTab={activeTabTopbets}
                    routeName={RouteNames}
                    preventRedirect={preventRedirect}
                    noRedirectFilter={noRedirectFilter}
                    isCategory={isCategory}
                    disciplineId={disciplineId}
                    extraClassName={`${isCategory ? 'CategoryId' : 'LocationId'}--${id}`}
                />
            )
        ));
    }

    const modifiers: BemModifiersMap = {
        Nav: true,
        ItemToStartUnequalWidth: true,
        Small10: true,
        Tertiary: true,
    };

    return (
        <Slider
            sliderItems={locationList}
            modifiersFromParent={modifiers}
            hasArrows={deviceType === 'desktop'}
        />
    );

}

const Nav: FC<TopBetNavPublicProps> = compose<FC<TopBetNavPublicProps>>(
    preLoader<TopBetNavPublicProps>({
        preload({ props, dispatch }): void {
            const { disciplineId, instanceId, isCategory } = props;

            dispatch(getTopBets(instanceId, disciplineId, isCategory));
        },
        getReadySlice({ instanceId }: TopBetNavPublicProps): string {
            const sliceName: string = getTopBetSliceName(instanceId);

            return `${sliceName}.ready`;
        },
        update({ props, prevProps, dispatch }): void {
            if (
                props.disciplineId !== prevProps.disciplineId
                || props.instanceId !== prevProps.instanceId
            ) {
                dispatch(removeTopBets(
                    prevProps.instanceId,
                ));
                dispatch(getTopBets(
                    props.instanceId,
                    props.disciplineId,
                    props.isCategory,
                ));
            }
        },
        destroy: ({ props, dispatch }) => {
            const { instanceId } = props;

            dispatch(removeTopBets(instanceId));
        },
    }),
    connect<TopBetNavStoreProps, {} ,TopBetNavPublicProps>(
        (state: AppState, props: TopBetNavPublicProps): TopBetNavStoreProps => {
            const { instanceId } = props
            const sliceName: string = getTopBetSliceName(instanceId);
            const slice: NavListStateModel | undefined = getSlice<NavListStateModel>(state, sliceName);
            const { deviceInfo, config: { lang } } = state;

            if (!slice || !deviceInfo) {
                return {};
            }

            const deviceType: PlatformTypes = deviceInfo.platform.type;

            return {
                list: slice.list,
                ready: slice.ready,
                deviceType,
                lang,
                disciplineId: slice.disciplineId,
                items: slice.items,
            };
        },
    ),
)(NavComponent);

export default Nav;
